import { countriesQuery } from "@/api/location";
import { supporterCountriesQuery } from "@/api/supporterCountries";
import { isAnyCountryEapEnabled, supporterEAPPeriodsQuery, supporterEAPSettingsQuery } from "@/api/supporterEap";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { EapSettingsTable } from "@/components/eap/EapSettingsTable";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { CountryFlagImage } from "@/components/ui/CountryFlagImage";
import { IconButton } from "@/components/ui/IconButton";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { CreateEAPPeriodDialog } from "@/pages/admin/supporters/[supporterId]/CreateEAPPeriodDialog";
import { EditEAPPeriodDialog } from "@/pages/admin/supporters/[supporterId]/EditEAPPeriodDialog";
import { formatDate, toLocalISOString } from "@common/utils/date";
import { Box } from "@givenwell/components";
import { PencilIcon } from "@givenwell/core-solid-icons";
import { Country, CountryRegistration, SupporterEapPeriod } from "@givenwell/management-api";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useState } from "react";

export const Route = createFileRoute("/admin/supporters/$supporterId/eap")({
  component: EAPPage,
  beforeLoad: () => ({
    breadcrumb: "EAP",
  }),
});

function EAPPage() {
  const { supporterId } = Route.useParams();
  const { data: registeredCountries } = useSuspenseQuery(supporterCountriesQuery(supporterId));
  const { data: countries } = useSuspenseQuery(countriesQuery(false));
  const isAdmin = useIsAdmin();

  return (
    <Box css={{ spaceY: 16, maxW: 1100 }}>
      <AvailabilityAlert registeredCountries={registeredCountries} countries={countries.countries} />

      <NoPeriodsAlert supporterId={supporterId} />

      <EapSettingsTable supporterId={supporterId} showEnabledColumn={isAdmin} />

      <PeriodsCard supporterId={supporterId} />
    </Box>
  );
}

function AvailabilityAlert({
  registeredCountries,
  countries,
}: {
  registeredCountries: CountryRegistration[];
  countries: Country[];
}) {
  const supportedCountryCodes = countries.filter(c => c.capabilities.eap_allowed).map(c => c.code);
  const registeredCountryCodes = registeredCountries.map(c => c.country);

  const unsupportedCountries = registeredCountryCodes.filter(country => !supportedCountryCodes.includes(country));

  if (unsupportedCountries.length === 0) {
    return null;
  }

  return (
    <Alert status="warning">
      Some registered countries don't support EAP:{" "}
      {unsupportedCountries.map((code, i) => (
        <Fragment key={i}>
          {i === 0 ? "" : ", "}
          <CountryFlagImage
            countryCode={code}
            css={{ size: 24, display: "inline-block", verticalAlign: "middle" }}
          />{" "}
          {code}
        </Fragment>
      ))}
    </Alert>
  );
}

/**
 * EAP is switched on for at least one country but the supporter has no periods, so members can't
 * actually request sessions until one is added.
 */
function NoPeriodsAlert({ supporterId }: { supporterId: string }) {
  const { data: settings } = useSuspenseQuery(supporterEAPSettingsQuery(supporterId));
  const { data: periods } = useSuspenseQuery(supporterEAPPeriodsQuery(supporterId));

  if (!isAnyCountryEapEnabled(settings) || periods.length > 0) {
    return null;
  }

  return (
    <Alert status="warning">
      EAP is enabled but there are no EAP periods. Members won't be able to use EAP until a period is added.
    </Alert>
  );
}

type PeriodsCardProps = { supporterId: string };
function PeriodsCard({ supporterId }: PeriodsCardProps) {
  const { data: periods } = useQuery(supporterEAPPeriodsQuery(supporterId));
  const [showPeriodDialog, setShowPeriodDialog] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<SupporterEapPeriod>();
  const lastPeriod = periods?.[periods.length - 1];
  const nextStartDate = lastPeriod ? addDays(new Date(lastPeriod.end_date), 1) : undefined;
  // Only periods that haven't started yet can be edited; the API rejects edits to current/past ones.
  const todayISO = toLocalISOString(new Date());

  return (
    <Card
      title="Periods"
      css={{ width: "100%" }}
      actions={
        <Button onClick={() => setShowPeriodDialog(true)} variant="tonal">
          Add Period
        </Button>
      }
    >
      <DataTable>
        <DataTableBody
          dataKey="period_id"
          data={periods ?? []}
          rounded="full"
          columns={[
            {
              header: "Period Date Range",
              cell: p => `${formatDate(p.start_date, true)} – ${formatDate(p.end_date, true)}`,
            },
            {
              header: "",
              css: { align: "right" },
              cell: p =>
                p.start_date > todayISO ? (
                  <IconButton
                    variant="plain"
                    color="neutral"
                    size="sm"
                    aria-label="Edit period"
                    onClick={() => setEditingPeriod(p)}
                  >
                    <PencilIcon />
                  </IconButton>
                ) : null,
            },
          ]}
        />
      </DataTable>
      {editingPeriod && (
        <EditEAPPeriodDialog
          key={editingPeriod.period_id}
          supporterId={supporterId}
          period={editingPeriod}
          open
          onOpenChange={open => {
            if (!open) setEditingPeriod(undefined);
          }}
        />
      )}
      <CreateEAPPeriodDialog
        key={nextStartDate?.toString()}
        open={showPeriodDialog}
        onOpenChange={setShowPeriodDialog}
        nextStartDate={nextStartDate}
      />
    </Card>
  );
}

function addDays(date: Date, days: number) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}
